import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Trash2, User as UserIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AvatarUploader({ user, editable = true, size = "lg", onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0); // cache buster

  const sizes = {
    sm: "h-10 w-10 text-sm",
    md: "h-14 w-14 text-lg",
    lg: "h-20 w-20 text-2xl",
    xl: "h-28 w-28 text-3xl",
  };
  const sizeCls = sizes[size] || sizes.lg;

  const hasAvatar = !!user.avatar_path;
  const src = hasAvatar
    ? `${BACKEND_URL}/api/users/${user.id}/avatar?v=${(user.avatar_url || "").split("v=")[1] || version}`
    : null;

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(file.type)) {
      toast.error("Nur JPG, PNG oder WebP");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild zu groß (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${user.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profilbild aktualisiert");
      setVersion((v) => v + 1);
      if (onChange) onChange(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Profilbild entfernen?")) return;
    setBusy(true);
    try {
      await api.delete(`/users/${user.id}/avatar`);
      toast.success("Profilbild entfernt");
      if (onChange) onChange({ ...user, avatar_path: "", avatar_url: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block group">
      <div className={`${sizeCls} rounded-full bg-gradient-to-br from-sky-700 to-teal-700 grid place-items-center text-white font-semibold overflow-hidden ring-2 ring-white shadow-sm`}>
        {hasAvatar ? (
          <img src={src} alt={user.name} className="h-full w-full object-cover" />
        ) : user.name ? (
          user.name[0]?.toUpperCase()
        ) : (
          <UserIcon className="h-1/2 w-1/2" />
        )}
      </div>
      {editable && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={upload}
            data-testid="avatar-file"
          />
          <button
            type="button"
            data-testid="avatar-upload"
            onClick={pick}
            disabled={busy}
            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-white border border-slate-200 grid place-items-center shadow-sm hover:bg-slate-50 text-slate-700"
            title="Bild ändern"
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
          {hasAvatar && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-white border border-slate-200 grid place-items-center shadow-sm hover:bg-red-50 text-red-600 opacity-0 group-hover:opacity-100 transition"
              title="Bild entfernen"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function AvatarBubble({ user, size = "sm", avatarUrl }) {
  const sizes = {
    xxs: "h-4 w-4 text-[9px]",
    xs: "h-7 w-7 text-xs",
    sm: "h-9 w-9 text-sm",
    md: "h-12 w-12 text-base",
  };
  const sizeCls = sizes[size] || sizes.sm;
  let src = null;
  if (avatarUrl) {
    src = avatarUrl.startsWith("http") ? avatarUrl : `${BACKEND_URL}${avatarUrl}`;
  } else if (user?.avatar_path) {
    src = `${BACKEND_URL}/api/users/${user.id}/avatar?v=${(user.avatar_url || "").split("v=")[1] || ""}`;
  }
  return (
    <div className={`${sizeCls} rounded-full bg-gradient-to-br from-sky-700 to-teal-700 grid place-items-center text-white font-semibold overflow-hidden flex-shrink-0`}>
      {src ? (
        <img src={src} alt={user?.name || ""} className="h-full w-full object-cover" />
      ) : (
        user?.name?.[0]?.toUpperCase() || "?"
      )}
    </div>
  );
}
